import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Rating,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { coursesAPI } from '../../services/api';
import { toast } from 'react-toastify';

// Styled components matching index.html design
const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: '448px', // max-w-md
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: theme.shadows[24],
  padding: '24px',
  outline: 'none',
  maxHeight: '90vh',
  overflow: 'auto',
}));

const RatingSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2d3748' : '#f7fafc',
  padding: '16px',
  borderRadius: '8px',
  margin: '16px 0',
}));


const RatingsModal = ({ 
  open, 
  onClose, 
  courseId, 
  courseRating = 0, 
  ratingCount = 0,
  userRating = 0,
  onSubmitRating,
  isEnrolled = false,
}) => {
  const { t } = useTranslation(['courses', 'common']);
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  
  const [currentRating, setCurrentRating] = useState(userRating);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentRating(userRating);
    }
  }, [open, userRating]);

  const handleSubmit = async (rating = currentRating) => {
    if (!rating || !isAuthenticated || !isEnrolled) {
      if (!isAuthenticated) {
        toast.info(t('courses:courseDetail.toasts.ratingLoginRequired'));
      } else if (!isEnrolled) {
        toast.info(t('courses:courseDetail.toasts.ratingEnrollmentRequired'));
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await coursesAPI.rate(courseId, rating);
      toast.success(t('courses:courseDetail.toasts.ratingSaved'));
      
      // Call the callback to refresh course data
      if (onSubmitRating) {
        onSubmitRating({
          rating: rating,
        });
      }
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      toast.error(t('courses:courseDetail.toasts.ratingError'));
      console.error('Error submitting rating:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="ratings-modal-title"
      aria-describedby="ratings-modal-description"
    >
      <ModalContainer>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="ratings-modal-title" variant="h5" component="h2" fontWeight="bold">
            {t('courses:courseDetail.ratingsModal.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              p: 1,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Box>

        {/* Course Rating Info */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <Rating
              value={courseRating}
              readOnly
              precision={0.5}
              size="large"
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#fbbf24', // yellow-400
                },
              }}
            />
            <Typography variant="h5" component="span" fontWeight="bold">
              {courseRating > 0 ? courseRating.toFixed(1) : '0.0'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {ratingCount} {ratingCount === 1 ? t('common:stats.review') : t('common:stats.reviews')}
          </Typography>
        </Box>

        {/* User Rating Form */}
        {isAuthenticated && isEnrolled && (
          <RatingSection>
            <Typography variant="h6" component="h3" textAlign="center" fontWeight={600} mb={2}>
              {userRating > 0 
                ? t('courses:courseDetail.ratingsModal.updateRating')
                : t('courses:courseDetail.ratingsModal.rateThisCourse')
              }
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Rating
                value={currentRating}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setCurrentRating(newValue);
                    handleSubmit(newValue);
                  }
                }}
                precision={0.5}
                size="large"
                disabled={isSubmitting}
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#fbbf24', // yellow-400
                  },
                  '& .MuiRating-iconEmpty': {
                    color: theme.palette.mode === 'dark' ? '#616161' : '#bdbdbd',
                  },
                  '& .MuiRating-icon': {
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  },
                }}
              />
            </Box>

            {isSubmitting && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('courses:courseDetail.ratingsModal.saving')}
                </Typography>
              </Box>
            )}
          </RatingSection>
        )}

        {/* Prompts for Non-Authenticated or Non-Enrolled Users */}
        {!isAuthenticated && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {t('courses:courseDetail.ratingsModal.loginToRate')}
            </Typography>
          </Box>
        )}

        {isAuthenticated && !isEnrolled && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {t('courses:courseDetail.ratingsModal.enrollToRate')}
            </Typography>
          </Box>
        )}

      </ModalContainer>
    </Modal>
  );
};

export default RatingsModal;
